/**
 * HelpHubCore.tsx
 *
 * Main Help Hub sidebar component.
 * Supports authenticated and guest modes.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHelpHub, HelpHubView, HELPHUB_WIDTH } from '../context/HelpHubContext';
import { HELP_HUB_CONFIG } from '../services/helpHubService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IconProps {
  name: string;
  size?: number;
  className?: string;
}

const Icon: React.FC<IconProps> = ({ name, size = 16, className }) => {
  const icons: Record<string, React.ReactElement> = {
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
    send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    bot: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><circle cx="8" cy="16" r="1" fill="currentColor"/><circle cx="16" cy="16" r="1" fill="currentColor"/></svg>,
    mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    lightbulb: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
    map: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
    megaphone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    chevronRight: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
    externalLink: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    headphones: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    book: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
    graduationCap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    layout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    thumbsUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>,
    messageCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
    loader: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>,
  };
  return <span className={className} style={{ display: 'inline-flex' }}>{icons[name] || null}</span>;
};

const getVars = (primaryColor: string = '#005fac') => ({
  primary: primaryColor,
  primaryLight: `${primaryColor}14`,
  primaryMuted: `${primaryColor}26`,
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  border: '#e2e8f0',
  text: '#1e293b',
  textMuted: '#64748b',
  textTertiary: '#94a3b8',
  success: '#22c55e',
  successBg: '#f0fdf4',
  warning: '#f59e0b',
  warningBg: '#fffbeb',
  error: '#ef4444',
  errorBg: '#fef2f2',
});

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB pre-compression cap
const MAX_ATTACHMENT_EDGE = 1600;             // px — longest edge after downscale

// Downscale + re-encode an image in-browser (no deps) so attachments stay small.
// Falls back to the original file if anything fails.
async function compressImage(file: File): Promise<{ data: string; filename: string; size: number }> {
  const readAsDataURL = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(new Error('read failed'));
      r.readAsDataURL(blob);
    });

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as any);
    const scale = Math.min(1, MAX_ATTACHMENT_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.85));
    if (!blob) throw new Error('toBlob failed');
    const data = await readAsDataURL(blob);
    return { data, filename: `${baseName}.webp`, size: blob.size };
  } catch {
    const data = await readAsDataURL(file);
    return { data, filename: file.name, size: file.size };
  }
}

// Derive a friendly first name. Pod logins arrive as a raw email
function friendlyFirstName(raw?: string): string {
  if (!raw) return '';
  let n = raw.trim();
  if (!n) return '';
  if (n.includes('@')) n = n.split('@')[0];   // email -> local part
  n = n.split(/[ ._-]+/)[0];                   // first token only
  if (!n) return '';
  return n.charAt(0).toUpperCase() + n.slice(1);
}

interface HelpHubCoreProps {
  mode?: 'modal' | 'embedded';
  enableAttachments?: boolean;
}

const SUGGESTION_CHIPS = [
  'How do I get started?',
  'Connect a data source',
  'Extend my trial',
];

const ACADEMY_TRACKS = [
  { id: 'admin', icon: 'settings', label: 'For Admins', desc: 'Setup, users, integrations', href: 'https://dashboardfox.com/academy/#track-admin' },
  { id: 'builder', icon: 'layout', label: 'For Builders', desc: 'Build dashboards & reports', href: 'https://dashboardfox.com/academy/#track-builder' },
  { id: 'enduser', icon: 'eye', label: 'For End Users', desc: 'View & explore data', href: 'https://dashboardfox.com/academy/#track-enduser' },
];

const mdBlock = { margin: '0 0 8px' };
const mdCode = { fontFamily: 'monospace', fontSize: '0.85em', background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: '4px' };
const mdHeading = ({ children }: any) => <div style={{ fontWeight: 600, margin: '4px 0 6px' }}>{children}</div>;
const mdCell = { border: '1px solid rgba(0,0,0,0.12)', padding: '4px 8px', textAlign: 'left' as const };

const BotMarkdown = ({ text, linkColor }: { text: string; linkColor: string }) => (
  <div style={{ marginBottom: '-8px' }}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p style={mdBlock}>{children}</p>,
        ul: ({ children }) => <ul style={{ ...mdBlock, paddingLeft: '20px' }}>{children}</ul>,
        ol: ({ children }) => <ol style={{ ...mdBlock, paddingLeft: '20px' }}>{children}</ol>,
        li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline' }}>{children}</a>
        ),
        img: ({ src, alt }) => (
          <a href={src} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline' }}>{alt || 'View image'}</a>
        ),
        code: ({ children }) => <code style={mdCode}>{children}</code>,
        pre: ({ children }) => (
          <pre style={{ ...mdBlock, padding: '8px', background: 'rgba(0,0,0,0.06)', borderRadius: '6px', overflowX: 'auto' }}>{children}</pre>
        ),
        h1: mdHeading, h2: mdHeading, h3: mdHeading, h4: mdHeading,
        table: ({ children }) => (
          <div style={{ ...mdBlock, overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', fontSize: '0.875rem' }}>{children}</table></div>
        ),
        th: ({ children }) => <th style={{ ...mdCell, fontWeight: 600 }}>{children}</th>,
        td: ({ children }) => <td style={mdCell}>{children}</td>,
      }}
    >
      {text}
    </ReactMarkdown>
  </div>
);

const HelpHubCore: React.FC<HelpHubCoreProps> = ({ mode = 'modal', enableAttachments = true }: HelpHubCoreProps) => {
  const {
    isOpen,
    currentView,
    messages,
    ticketForm,
    consentGiven,
    config,
    isTyping,
    systemStatus,
    frillToken,
    userTickets,
    ticketsLoading,
    unreadChangelog,
    pendingChatMessage,
    close,
    open,
    setView,
    goHome,
    addMessage,
    updateTicketForm,
    resetTicketForm,
    acceptConsent,
    setIsTyping,
    setPendingChatMessage,
    markChangelogRead,
    hasHumanChatAccess,
    userTier,
    isGuest,
    apiService,
  } = useHelpHub();

  const vars = getVars(config.brand.primaryColor);
  const [isAnimating, setIsAnimating] = useState(false);
const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<{ data: string; filename: string; size: number }[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [homeInput, setHomeInput] = useState('');
  const [humanLoading, setHumanLoading] = useState(false);
  const [humanError, setHumanError] = useState<string | null>(null);
  const [concordFunctional, setConcordFunctional] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  useEffect(() => {
    const concord = (window as any).concord;
    if (concord?.consent?.onConsent) {
      concord.consent.onConsent('functional', () => setConcordFunctional(true));
    }
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Payment pages (PCI DSS 6.4.3): live chat is not loaded on the pod billing page.
  const isPaymentPage = typeof window !== "undefined" && /\/billing\.html$/i.test(window.location.pathname);

  // Preload HelpCrunch SDK for authenticated users (escalation flow)
  useEffect(() => {
    if (!apiService) return;
    if (!isGuest && !isPaymentPage) {
      apiService.loadHelpCrunchSDK?.().catch(() => {});
    }
  }, [isGuest, apiService, isPaymentPage]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus chat input when entering chat view OR after consent is granted
  useEffect(() => {
    if (currentView === 'chat' && consentGiven) {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [currentView, consentGiven]);

  const chatSessionIdRef = useRef<string | undefined>(undefined);

  const handleSendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? chatInput).trim();
    if (!text || isTyping) return;

    if (overrideText === undefined) {
      setChatInput('');
    }
    addMessage(text, true);
    setIsTyping(true);

    try {
      const conversationHistory = messages.map(m => ({
        role: (m.isUser ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.text,
      }));
      conversationHistory.push({ role: 'user', content: text });

      if (!apiService) return;
      // New chat (no prior messages) -> start a new Chatbase conversation
      if (messages.length === 0) chatSessionIdRef.current = undefined;
      const response = await apiService.sendChatMessage({
        message: text,
        conversationHistory,
        sessionId: chatSessionIdRef.current,
      }, isGuest);
      if (response.sessionId) chatSessionIdRef.current = response.sessionId;

      addMessage(response.text, false);
    } catch (error: any) {
      console.error('Chat error:', error);
      if (error.message?.includes('limit')) {
        addMessage("You've reached the chat limit. Sign up for unlimited access!", false);
      } else {
        addMessage("I'm having trouble connecting. Please try again or submit a ticket.", false);
      }
    } finally {
      setIsTyping(false);
    }
  };

  // Auto-send pending message when chat view is active and consent is granted.
  // Covers both paths: (1) consent already given on home → enter chat → send,
  // (2) home → chat → consent gate → accept → send.
  useEffect(() => {
    if (currentView === 'chat' && consentGiven && pendingChatMessage) {
      const msg = pendingChatMessage;
      setPendingChatMessage(null);
      handleSendMessage(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, consentGiven, pendingChatMessage]);

  const handleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startChatWithMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setPendingChatMessage(trimmed);
    setHomeInput('');
    setView('chat');
  };

  const handleHomeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      startChatWithMessage(homeInput);
    }
  };

const handleSubmitTicket = async () => {
    if (!ticketForm.subject.trim() || !ticketForm.message.trim() || !ticketForm.email.trim()) {
      return;
    }

    setTicketSubmitting(true);
    try {
      if (!apiService) return;
      await apiService.submitTicket({
        subject: ticketForm.subject,
        message: ticketForm.message,
        email: ticketForm.email,
        userName: config.user.name,
        userTier,
        category: ticketForm.category,
        attachments: attachments.map(a => ({ data: a.data, filename: a.filename })),
      });
      setTicketSubmitted(true);
    } catch (error) {
      console.error('Ticket error:', error);
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttachmentError(null);
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (!files.length) return;

    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) { setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} images.`); return; }

    for (const f of files.slice(0, room)) {
      if (!f.type.startsWith('image/')) { setAttachmentError('Only image files can be attached.'); continue; }
      if (f.size > MAX_ATTACHMENT_BYTES) { setAttachmentError('Each image must be under 5 MB.'); continue; }
      try {
        const compressed = await compressImage(f);
        setAttachments(prev => (prev.length < MAX_ATTACHMENTS ? [...prev, compressed] : prev));
      } catch {
        setAttachmentError('Could not process that image.');
      }
    }
    if (files.length > room) {
      setAttachmentError(`Only ${room} more image${room === 1 ? '' : 's'} could be added (max ${MAX_ATTACHMENTS}).`);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEscalateToHuman = async () => {
    if (isPaymentPage) {
      setHumanError("Live chat is not available on the billing page. Open the Help Hub from any other page to chat with us.");
      return;
    }
    if (!hasHumanChatAccess) {
      setHumanError('Live support is available for Premium plans or accounts with multiple workspaces.');
      return;
    }

    setHumanLoading(true);
    setHumanError(null);

    try {
      if (!apiService) return;
      await apiService.loadHelpCrunchSDK?.();
      apiService.openHelpCrunch?.(config.user);
      apiService.addHelpCrunchCloseBar?.(() => { open(); setView('chat'); });
      close();
    } catch (error) {
      console.error('Escalation error:', error);
      setHumanError('Unable to connect. Please try again.');
    } finally {
      setHumanLoading(false);
    }
  };

const triggerBugReport = () => {
    const beb = (window as any).birdeatsbug;
    // SDK absent (ad-blocker, or a pod page that doesn't load it) -> fall back
    // to the ticket form instead of a dead click.
    if (!beb || typeof beb.trigger !== 'function') {
      setView('ticket');
      return;
    }
    close();
    setTimeout(() => {
      try {
        if (config.user.email) {
          beb.setOptions({ user: { email: config.user.email } });
        }
        beb.trigger();
      } catch {
        open();
        setView('ticket');
      }
    }, 300);
  };

const greetingName = friendlyFirstName(config.user.name);
  const viewInfo: Record<HelpHubView, { title: string; subtitle: string }> = {
    home: {
      title: greetingName ? `Hi ${greetingName}! 👋` : 'Hi there! 👋',
      subtitle: 'How can we help?'
    },
    chat: { title: 'AI Assistant', subtitle: '🤖 Powered by AI' },
    tickets: { title: 'My Tickets', subtitle: 'Track your requests' },
    ticket: { title: 'Submit a Ticket', subtitle: "We'll respond soon" },
    human: { title: 'Live Support', subtitle: '👤 Talking to a human' },
  };

  const info = viewInfo[currentView];

  const Badge: React.FC<{ count: number | string; color?: string }> = ({ count, color = vars.primary }) => (
    <span style={{
      minWidth: '18px',
      height: '18px',
      padding: '0 6px',
      borderRadius: '9px',
      backgroundColor: color,
      color: '#fff',
      fontSize: '0.6875rem',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {count}
    </span>
  );

  const StatusIndicator: React.FC = () => {
    if (!systemStatus) return null;

    const statusColors = {
      operational: { bg: vars.successBg, color: vars.success, text: 'All Systems Operational' },
      degraded: { bg: vars.warningBg, color: vars.warning, text: 'Partial Outage' },
      down: { bg: vars.errorBg, color: vars.error, text: 'Major Outage' },
    };
    const s = statusColors[systemStatus.status];

    return (
      <a
        href={HELP_HUB_CONFIG.LINKS.statusPage}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: s.bg,
          borderRadius: '8px',
          fontSize: '0.8125rem',
          textDecoration: 'none',
        }}
      >
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: s.color,
        }} />
        <span style={{ color: s.color, fontWeight: 500 }}>{s.text}</span>
        <span style={{ color: vars.textMuted, marginLeft: 'auto', fontSize: '0.75rem' }}>
          {systemStatus.uptime}
        </span>
      </a>
    );
  };

  const Eyebrow: React.FC<{ children: React.ReactNode; tone?: 'muted' | 'primary' }> = ({ children, tone = 'muted' }) => (
    <div style={{
      fontSize: '0.75rem',
      fontWeight: 600,
      color: tone === 'primary' ? vars.primary : vars.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '10px',
    }}>
      {children}
    </div>
  );

  // ============================================
  // HOME
  // ============================================
  const renderHome = () => {
    // Resource tile config — order matches mockup
    const roadmapUrl = frillToken?.urls?.roadmap || HELP_HUB_CONFIG.LINKS.roadmap;
    const changelogUrl = frillToken?.urls?.changelog || HELP_HUB_CONFIG.LINKS.changelog;
    const ideasUrl = frillToken?.urls?.ideas || HELP_HUB_CONFIG.LINKS.ideas;

    type Tile = {
      id: string;
      icon: string;
      label: string;
      desc: string;
      badge?: number;
      external?: string;
      onClick?: () => void;
    };

    const resourceTiles: Tile[] = [
      { id: 'kb', icon: 'book', label: 'Knowledge Base', desc: 'Browse articles', external: HELP_HUB_CONFIG.LINKS.knowledgeBase },
      { id: 'roadmap', icon: 'map', label: 'Roadmap', desc: "See what's coming", external: roadmapUrl },
      {
        id: 'changelog', icon: 'megaphone', label: "What's New", desc: 'Latest updates',
        badge: unreadChangelog || undefined, external: changelogUrl,
        onClick: () => { markChangelogRead(); },
      },
      { id: 'ideas', icon: 'lightbulb', label: 'Ideas & Voting', desc: 'Shape our roadmap', external: ideasUrl },
{
        id: 'community', icon: 'users', label: 'Community', desc: 'Ask other users',
        onClick: async () => {
if (isGuest || !apiService) { window.open(HELP_HUB_CONFIG.LINKS.community, '_blank', 'noopener'); return; }
          try {
            const { token } = await apiService.getBetterModeToken();
            window.open(apiService.getCommunitySSOUrl(token), '_blank', 'noopener');
          } catch {
            window.open(HELP_HUB_CONFIG.LINKS.community, '_blank', 'noopener');
          }
        },
      },
      isGuest
        ? { id: 'submit-ticket', icon: 'mail', label: 'Submit a Ticket', desc: 'Get help from our team', onClick: () => setView('ticket') }
        : { id: 'tickets', icon: 'clock', label: 'My Tickets', desc: 'Track requests', onClick: () => setView('tickets') },
    ];

    const showBugReport = !isGuest && (concordFunctional || consentGiven);


    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {config.features.systemStatus && <StatusIndicator />}

        {/* AI HERO — flat tinted card */}
        <div style={{
          padding: '18px',
          backgroundColor: `${vars.primary}0f`,
          border: `1px solid ${vars.primary}26`,
          borderRadius: '12px',
        }}>
          <Eyebrow tone="primary">Ask AI</Eyebrow>
          <div style={{
            fontSize: '0.9375rem',
            color: vars.text,
            marginBottom: '14px',
            lineHeight: 1.4,
          }}>
            Ask anything about DashboardFox
          </div>
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '4px 4px 4px 14px',
            backgroundColor: vars.surface,
            border: `1px solid ${vars.primary}26`,
            borderRadius: '10px',
            alignItems: 'center',
            marginBottom: '12px',
          }}>
            <input
              type="text"
              value={homeInput}
              onChange={(e) => setHomeInput(e.target.value)}
              onKeyPress={handleHomeKeyPress}
              placeholder="Type your question..."
              className="dfx-helphub-focus"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '0.9375rem',
                backgroundColor: 'transparent',
                padding: '10px 0',
                fontFamily: 'inherit',
                color: vars.text,
                minWidth: 0,
              }}
            />
            <button
              onClick={() => startChatWithMessage(homeInput)}
              disabled={!homeInput.trim()}
              aria-label="Send"
              className="dfx-helphub-focus"
              style={{
                padding: '10px 14px',
                backgroundColor: vars.primary,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: homeInput.trim() ? 'pointer' : 'not-allowed',
                opacity: homeInput.trim() ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="send" size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {SUGGESTION_CHIPS.map((chip) => (
              <button
                key={chip}
                onClick={() => startChatWithMessage(chip)}
                className="dfx-helphub-focus"
                style={{
                  padding: '7px 12px',
                  backgroundColor: vars.surface,
                  border: `1px solid ${vars.primary}40`,
                  borderRadius: '999px',
                  fontSize: '0.8125rem',
                  color: vars.text,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = vars.primaryLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = vars.surface; }}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>

        {/* ACADEMY — tinted surface */}
        <div style={{
          padding: '18px',
          backgroundColor: vars.surfaceAlt,
          border: `1px solid ${vars.border}`,
          borderRadius: '12px',
        }}>
          <Eyebrow>DashboardFox Academy</Eyebrow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {ACADEMY_TRACKS.map((track) => (
              <a
                key={track.id}
                href={track.href}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setHoveredItem(`academy-${track.id}`)}
                onMouseLeave={() => setHoveredItem(null)}
                className="dfx-helphub-focus"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  backgroundColor: vars.surface,
                  border: `1px solid ${vars.border}`,
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  transform: hoveredItem === `academy-${track.id}` ? 'translateY(-1px)' : 'none',
                  boxShadow: hoveredItem === `academy-${track.id}` ? '0 2px 6px rgba(15,23,42,0.06)' : 'none',
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: vars.primaryLight,
                  border: `1px solid ${vars.primary}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: vars.primary,
                  flexShrink: 0,
                }}>
                  <Icon name={track.icon} size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: vars.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {track.label}
                    <Icon name="externalLink" size={12} />
                  </div>
                  <div style={{ fontSize: '0.75rem', color: vars.textMuted }}>{track.desc}</div>
                </div>
              </a>
            ))}
          </div>
          <a
            href="https://dashboardfox.com/academy/"
            target="_blank"
            rel="noopener noreferrer"
            className="dfx-helphub-focus"
            style={{
              display: 'block',
              fontSize: '0.8125rem',
              color: vars.primary,
              fontWeight: 500,
              textAlign: 'center',
              textDecoration: 'none',
              marginTop: '12px',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            Browse all courses →
          </a>
        </div>

        {/* MORE RESOURCES */}
        <div>
          <Eyebrow>More Resources</Eyebrow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {resourceTiles.map((item) => {
              const handleClick = (e: React.MouseEvent) => {
                if (item.onClick) item.onClick();
                if (item.external) {
                  // Allow native anchor behavior
                  return;
                }
                if (!item.external) {
                  e.preventDefault();
                }
              };

              const isHovered = hoveredItem === item.id;

              const tileContent = (
                <>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: vars.surfaceAlt,
                    border: `1px solid ${vars.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: vars.textMuted,
                  }}>
                    <Icon name={item.icon} size={16} />
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: vars.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      {item.label}
                      {item.badge ? <Badge count={item.badge} /> : null}
                      {item.external && <Icon name="externalLink" size={12} />}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: vars.textMuted, marginTop: '2px' }}>
                      {item.desc}
                    </div>
                  </div>
                </>
              );

              const tileStyle: React.CSSProperties = {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '16px',
                backgroundColor: vars.surface,
                border: `1px solid ${vars.border}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                textAlign: 'left',
                textDecoration: 'none',
                color: 'inherit',
                transform: isHovered ? 'translateY(-1px)' : 'none',
                boxShadow: isHovered ? '0 2px 6px rgba(15,23,42,0.06)' : 'none',
              };

              return item.external ? (
                <a
                  key={item.id}
                  href={item.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="dfx-helphub-focus"
                  style={tileStyle}
                >
                  {tileContent}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={handleClick}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="dfx-helphub-focus"
                  style={tileStyle as any}
                >
                  {tileContent}
                </button>
              );
            })}
          </div>
        </div>

        {/* DASHED CTA — Bug Report (auth + concord) OR Submit a Ticket fallback */}
        {showBugReport ? (
          <button
            onClick={triggerBugReport}
            onMouseEnter={() => setHoveredItem('bug-report')}
            onMouseLeave={() => setHoveredItem(null)}
            className="dfx-helphub-focus"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: hoveredItem === 'bug-report' ? vars.surfaceAlt : 'transparent',
              border: `1px dashed ${vars.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              color: vars.textMuted,
              fontSize: '0.875rem',
              transition: 'background-color 0.15s',
            }}
          >
            <Icon name="alert" size={16} />
            Found a bug? Record & send a report
          </button>
        ) : isGuest ? null : (
          <button
            onClick={() => setView('ticket')}
            onMouseEnter={() => setHoveredItem('submit-ticket-cta')}
            onMouseLeave={() => setHoveredItem(null)}
            className="dfx-helphub-focus"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: hoveredItem === 'submit-ticket-cta' ? vars.surfaceAlt : 'transparent',
              border: `1px dashed ${vars.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              color: vars.textMuted,
              fontSize: '0.875rem',
              transition: 'background-color 0.15s',
            }}
          >
            <Icon name="mail" size={16} />
            Can't find an answer? Submit a ticket
          </button>
        )}

        {isGuest && (
          <a
            href="https://dashboardfox.com/signup"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: vars.primaryLight,
              border: `1px solid ${vars.primaryMuted}`,
              borderRadius: '8px',
              color: vars.primary,
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            🚀 Want full access? Start free →
          </a>
        )}
      </div>
    );
  };

  // ============================================
  // CONSENT GATE
  // ============================================
  const renderConsentGate = () => (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🔒</div>
      <h3 style={{ margin: '0 0 12px', fontSize: '1.125rem', fontWeight: 600, color: vars.text }}>
        Privacy Notice
      </h3>
      <p style={{ fontSize: '0.875rem', color: vars.textMuted, lineHeight: 1.6, marginBottom: '16px' }}>
        To provide support, your messages may be processed by our AI assistant and support team.
      </p>
      <ul style={{ textAlign: 'left', fontSize: '0.8125rem', color: vars.textMuted, lineHeight: 1.8, margin: '0 auto 16px', maxWidth: '320px', paddingLeft: '20px' }}>
        <li>Chat messages are processed by our AI partner</li>
        <li>Tickets are sent to our support system</li>
        <li>We do not share your data with third parties for marketing</li>
      </ul>
      <div style={{
        background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px',
        padding: '12px', margin: '0 0 20px', fontSize: '0.8125rem', color: '#92400e', textAlign: 'left'
      }}>
        <strong>⚠️ Important:</strong> Please do not share sensitive information such as passwords, social security numbers, patient health information, or student records in chat.
      </div>
      <button
        onClick={acceptConsent}
        style={{
          background: vars.primary, color: '#fff', border: 'none', borderRadius: '8px',
          padding: '12px 24px', fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer',
          width: '100%', marginBottom: '12px',
        }}
      >
        I Understand, Continue
      </button>
      <a
        href="https://dashboardfox.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: '0.8125rem', color: vars.primary, textDecoration: 'none' }}
      >
        View Privacy Policy
      </a>
    </div>
  );

  // ============================================
  // CHAT
  // ============================================
  const renderChat = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', margin: 0, overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', color: vars.textMuted }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🤖</div>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>
              Hi! I'm your AI assistant. How can I help you today?
            </p>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} style={{ display: 'flex', justifyContent: msg.isUser ? 'flex-end' : 'flex-start', gap: '10px' }}>
            {!msg.isUser && (
              <div style={{
                width: '32px', height: '32px', borderRadius: '10px',
                background: `linear-gradient(135deg, ${vars.primary} 0%, #3b82f6 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff',
              }}>
                <Icon name="bot" size={18} />
              </div>
            )}
            <div style={{
              maxWidth: '80%', padding: '12px 16px',
              backgroundColor: msg.isUser ? vars.primary : vars.surfaceAlt,
              color: msg.isUser ? '#fff' : vars.text,
              borderRadius: msg.isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              fontSize: '0.9375rem', lineHeight: 1.5,
              whiteSpace: msg.isUser ? 'pre-wrap' : 'normal',
              wordBreak: 'break-word',
            }}>
              {msg.isUser ? msg.text : <BotMarkdown text={msg.text} linkColor={vars.primary} />}
            </div>
          </div>
        ))}
        {isTyping && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '10px',
              background: `linear-gradient(135deg, ${vars.primary} 0%, #3b82f6 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff',
            }}>
              <Icon name="bot" size={18} />
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '12px 16px', backgroundColor: vars.surfaceAlt, borderRadius: '16px',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: vars.textTertiary, animation: 'bounce 1.4s infinite' }} />
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: vars.textTertiary, animation: 'bounce 1.4s infinite 0.2s' }} />
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: vars.textTertiary, animation: 'bounce 1.4s infinite 0.4s' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {hasHumanChatAccess && messages.length >= 3 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', backgroundColor: consentGiven ? vars.successBg : vars.surfaceAlt, borderTop: consentGiven ? '1px solid #bbf7d0' : `1px solid ${vars.border}`,
        }}>
          <span style={{ fontSize: '0.875rem', color: vars.text }}>Need a human?</span>
          {consentGiven ? (
            <button
              onClick={handleEscalateToHuman}
              disabled={humanLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                backgroundColor: vars.success, color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '0.8125rem', fontWeight: 600, cursor: humanLoading ? 'wait' : 'pointer',
                opacity: humanLoading ? 0.7 : 1,
              }}
            >
              {humanLoading ? <Icon name="loader" size={14} /> : <Icon name="headphones" size={14} />}
              Live Support
            </button>
          ) : (
            <button
              onClick={() => setView('ticket')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                backgroundColor: vars.primary, color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Icon name="mail" size={14} />
              Submit a Ticket
            </button>
          )}
        </div>
      )}

      {humanError && (
        <div style={{ padding: '10px 16px', backgroundColor: vars.errorBg, color: vars.error, fontSize: '0.875rem', textAlign: 'center' }}>
          {humanError}
        </div>
      )}
      {!hasHumanChatAccess && messages.length >= 3 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', backgroundColor: vars.surfaceAlt, borderTop: `1px solid ${vars.border}`,
        }}>
          <span style={{ fontSize: '0.875rem', color: vars.text }}>Need more help?</span>
          <button
            onClick={() => setView('ticket')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              backgroundColor: vars.primary, color: '#fff', border: 'none', borderRadius: '8px',
              fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Icon name="mail" size={14} />
            Submit Ticket
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', padding: '16px', borderTop: `1px solid ${vars.border}`, backgroundColor: vars.surface }}>
        <textarea
          ref={chatInputRef}
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyPress={handleChatKeyPress}
          placeholder="Ask me anything..."
          rows={1}
          style={{
            flex: 1, padding: '12px 16px', border: `1px solid ${vars.border}`, borderRadius: '10px',
            fontSize: '0.9375rem', outline: 'none', resize: 'none', fontFamily: 'inherit',
          }}
        />
        <button
          onClick={() => handleSendMessage()}
          disabled={!chatInput.trim() || isTyping}
          style={{
            padding: '12px 16px', backgroundColor: vars.primary, color: '#fff', border: 'none',
            borderRadius: '10px', cursor: chatInput.trim() && !isTyping ? 'pointer' : 'not-allowed',
            opacity: chatInput.trim() && !isTyping ? 1 : 0.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="send" size={18} />
        </button>
      </div>
    </div>
  );

  // ============================================
  // TICKETS LIST
  // ============================================
const renderTickets = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ margin: 0, fontSize: '0.875rem', color: vars.textMuted, lineHeight: 1.5 }}>
        {userTickets.length > 0 ? (
          <>
            Tap a ticket to view or reply in the support portal — you'll confirm your email
            {config.user.email ? <> (<span style={{ color: vars.text, fontWeight: 500 }}>{config.user.email}</span>)</> : null} to log in.
          </>
        ) : (
          'Track your support requests'
        )}
      </p>

      {ticketsLoading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: vars.textMuted }}>
          <Icon name="loader" size={24} />
        </div>
      ) : userTickets.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: vars.textMuted }}>
          <p>No tickets yet</p>
        </div>
      ) : (
        userTickets.map(ticket => {
          const isHovered = hoveredItem === `ticket-${ticket.id}`;
          return (
            <a
              key={ticket.id}
              href={HELP_HUB_CONFIG.LINKS.ticketPortal}
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => setHoveredItem(`ticket-${ticket.id}`)}
              onMouseLeave={() => setHoveredItem(null)}
              className="dfx-helphub-focus"
              style={{
                display: 'block',
                padding: '16px', backgroundColor: vars.surface, border: `1px solid ${vars.border}`, borderRadius: '10px',
                textDecoration: 'none', color: 'inherit', cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                transform: isHovered ? 'translateY(-1px)' : 'none',
                boxShadow: isHovered ? '0 2px 6px rgba(15,23,42,0.06)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: vars.textTertiary, fontFamily: 'monospace' }}>{ticket.id}</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '999px',
                  fontSize: '0.6875rem', fontWeight: 600,
                  backgroundColor: ticket.status === 'open' || ticket.status === 'in-progress' ? vars.warningBg : vars.successBg,
                  color: ticket.status === 'open' || ticket.status === 'in-progress' ? vars.warning : vars.success,
                }}>
                  <Icon name={ticket.status === 'resolved' || ticket.status === 'closed' ? 'check' : 'alert'} size={12} />
                  {ticket.status}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.9375rem', fontWeight: 500, color: vars.text }}>{ticket.subject}</span>
                <span style={{ color: vars.textTertiary, display: 'inline-flex', flexShrink: 0 }}>
                  <Icon name="externalLink" size={14} />
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: vars.textTertiary }}>
{ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString() : ''}
              </div>
            </a>
          );
        })
      )}

      <button
        onClick={() => setView('ticket')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px',
          backgroundColor: vars.primary, color: '#fff', border: 'none', borderRadius: '10px',
          cursor: 'pointer', fontSize: '0.9375rem', fontWeight: 600,
        }}
      >
        <Icon name="mail" size={18} />
        Submit New Ticket
      </button>
    </div>
  );

  // ============================================
  // TICKET FORM
  // ============================================
  const renderTicketForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {ticketSubmitted ? (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
          <h3 style={{ margin: '0 0 8px', color: vars.text }}>Ticket Submitted!</h3>
          <p style={{ margin: '0 0 16px', color: vars.textMuted }}>We'll get back to you soon.</p>
          <button
onClick={() => { resetTicketForm(); setTicketSubmitted(false); setAttachments([]); setAttachmentError(null); }}
            style={{
              padding: '10px 20px', backgroundColor: vars.surfaceAlt, border: `1px solid ${vars.border}`,
              borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            Submit Another
          </button>
        </div>
      ) : (
        <>
          <div>
            <label style={{
              display: 'block', fontSize: '0.75rem', fontWeight: 600, color: vars.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
            }}>Category</label>
            <select
              value={ticketForm.category}
              onChange={(e) => updateTicketForm({ category: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', border: `1px solid ${vars.border}`, borderRadius: '8px',
                fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
                backgroundColor: vars.surface, color: vars.text,
              }}
            >
              <option value="">Select a category...</option>
              <option value="technical">Technical Issue</option>
              <option value="billing">Billing</option>
              <option value="feature-request">Feature Request</option>
              <option value="general">General Question</option>
            </select>
          </div>
          <div>
            <label style={{
              display: 'block', fontSize: '0.75rem', fontWeight: 600, color: vars.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
            }}>Subject</label>
            <input
              type="text"
              placeholder="Brief description of your issue"
              value={ticketForm.subject}
              onChange={(e) => updateTicketForm({ subject: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', border: `1px solid ${vars.border}`, borderRadius: '8px',
                fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block', fontSize: '0.75rem', fontWeight: 600, color: vars.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
            }}>Message</label>
            <textarea
              placeholder="Please describe your issue in detail..."
              rows={6}
              value={ticketForm.message}
              onChange={(e) => updateTicketForm({ message: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', border: `1px solid ${vars.border}`, borderRadius: '8px',
                fontSize: '0.9375rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block', fontSize: '0.75rem', fontWeight: 600, color: vars.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
            }}>Email</label>
            <input
              type="email"
              value={ticketForm.email}
              onChange={(e) => updateTicketForm({ email: e.target.value })}
style={{
                width: '100%', padding: '12px 14px', border: `1px solid ${vars.border}`, borderRadius: '8px',
                fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
                backgroundColor: isGuest ? vars.surface : vars.surfaceAlt,
              }}
            />
          </div>

          {!isGuest && enableAttachments && (
            <div>
              <label style={{
                display: 'block', fontSize: '0.75rem', fontWeight: 600, color: vars.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
              }}>Attachments (optional)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleAttachmentChange}
                disabled={attachments.length >= MAX_ATTACHMENTS}
                style={{ fontSize: '0.8125rem', color: vars.textMuted }}
              />
              <div style={{ fontSize: '0.75rem', color: vars.textTertiary, marginTop: '6px' }}>
                Up to {MAX_ATTACHMENTS} images, 5 MB each. Screenshots are resized automatically.
              </div>
              {attachmentError && (
                <div style={{ fontSize: '0.75rem', color: vars.error, marginTop: '6px' }}>{attachmentError}</div>
              )}
              {attachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                  {attachments.map((att, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                      padding: '8px 10px', backgroundColor: vars.surfaceAlt, border: `1px solid ${vars.border}`, borderRadius: '8px',
                    }}>
                      <span style={{ fontSize: '0.8125rem', color: vars.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {att.filename} ({(att.size / 1024).toFixed(0)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        aria-label="Remove attachment"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: vars.textMuted, display: 'inline-flex', padding: '2px', flexShrink: 0 }}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleSubmitTicket}
            disabled={ticketSubmitting || !ticketForm.subject || !ticketForm.message || !ticketForm.email}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px',
              backgroundColor: vars.primary, color: '#fff', border: 'none', borderRadius: '10px',
              cursor: ticketSubmitting ? 'wait' : 'pointer', fontSize: '0.9375rem', fontWeight: 600,
              opacity: ticketSubmitting || !ticketForm.subject || !ticketForm.message || !ticketForm.email ? 0.6 : 1,
            }}
          >
            {ticketSubmitting ? <Icon name="loader" size={18} /> : <Icon name="send" size={18} />}
            Submit Ticket
          </button>
        </>
      )}
    </div>
  );

  // ============================================
  // HUMAN
  // ============================================
  const renderHuman = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '24px' }}>
      <div style={{
        width: '64px', height: '64px', borderRadius: '50%', backgroundColor: vars.successBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
        color: vars.success, fontSize: '1.5rem',
      }}>
        <Icon name="headphones" size={32} />
      </div>
      <h3 style={{ margin: '0 0 8px', color: vars.text }}>Connected to Live Support</h3>
      <p style={{ margin: '0 0 24px', color: vars.textMuted, fontSize: '0.875rem' }}>
        Our support team will respond shortly
      </p>
      <button
        onClick={() => { apiService?.closeHelpCrunchAndReturn?.(); setView('chat'); }}
        style={{
          padding: '10px 20px', backgroundColor: vars.surfaceAlt, border: `1px solid ${vars.border}`,
          borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem',
        }}
      >
        Back to AI Chat
      </button>
    </div>
  );

  if (!isOpen && mode !== 'embedded') return null;

  const panel = (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: HELPHUB_WIDTH,
      height: '100vh',
      backgroundColor: vars.surface,
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 2147483646,
      display: 'flex',
      flexDirection: 'column',
      transform: isAnimating ? 'translateX(100%)' : 'translateX(0)',
      transition: 'transform 0.3s ease-out',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        padding: '14px 16px',
        borderBottom: `1px solid ${vars.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {currentView !== 'home' ? (
            <button onClick={goHome} style={{
              background: 'none', border: 'none', padding: '6px', cursor: 'pointer',
              borderRadius: '6px', color: vars.textMuted, display: 'flex',
            }}>
              <Icon name="arrowLeft" size={20} />
            </button>
          ) : (
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              background: `linear-gradient(135deg, ${vars.primary} 0%, #3b82f6 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '0.875rem', fontWeight: 700,
            }}>
              {config.brand.logoSmall ? (
                <img src={config.brand.logoSmall} alt="" style={{ width: '24px', height: '24px' }} />
              ) : (
                'DF'
              )}
            </div>
          )}
          <div>
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: vars.text }}>{info.title}</h3>
            <span style={{ fontSize: '0.75rem', color: vars.textMuted }}>{info.subtitle}</span>
          </div>
        </div>
        <button onClick={close} style={{
          background: 'none', border: 'none', padding: '6px', cursor: 'pointer',
          borderRadius: '6px', color: vars.textMuted, display: 'flex',
        }}>
          <Icon name="x" size={20} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', overflowX: 'hidden', padding: currentView === 'chat' ? 0 : '20px' }}>
        {currentView === 'home' && renderHome()}
        {currentView === 'chat' && (consentGiven ? renderChat() : renderConsentGate())}
        {currentView === 'tickets' && (consentGiven ? renderTickets() : renderConsentGate())}
        {currentView === 'ticket' && (consentGiven ? renderTicketForm() : renderConsentGate())}
        {currentView === 'human' && (consentGiven ? renderHuman() : renderConsentGate())}
      </div>
    </div>
  );

  return mode === 'modal' ? createPortal(panel, document.body) : panel;
};

if (typeof document !== 'undefined' && !document.getElementById('helphub-keyframes')) {
  const style = document.createElement('style');
  style.id = 'helphub-keyframes';
  style.textContent = `
    @keyframes bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .animate-spin { animation: spin 1s linear infinite; }
    .dfx-helphub-focus:focus-visible {
      outline: 2px solid #005fac;
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(style);
}

export default HelpHubCore;
